import { Link } from "react-router-dom";
import type { Article } from "@/lib/news";
import { getArticleImage, getLatestArticles, formatDate } from "@/lib/news";

const SectionHeading = () => (
  <div className="mb-6 flex items-end justify-between gap-4">
    <div>
      <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
        焦点专题
      </h2>
      <p className="mt-2 text-sm text-foreground-500">
        精选最新、最受关注的黑料动态，快速把握事件脉络。
      </p>
    </div>
    <span className="hidden items-center gap-1 text-xs font-label text-accent-600 sm:flex">
      <i className="ri-fire-line" />
      实时推荐
    </span>
  </div>
);

export default function FocusSection() {
  const focus = getLatestArticles(5);
  const [lead, ...rest] = focus;

  if (!lead) return null;

  return (
    <section className="mx-auto w-full max-w-[1200px] px-4 py-12 md:px-6 md:py-16">
      <SectionHeading />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <LeadCard article={lead} />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
          {rest.map((article, index) => (
            <MiniCard key={article.id} article={article} order={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

function LeadCard({ article }: { article: Article }) {
  return (
    <Link
      to={`/detail/${article.id}`}
      className="group relative flex min-h-[280px] flex-col justify-end overflow-hidden rounded-lg border border-background-200 animate-fade-up cursor-pointer md:min-h-[420px]"
    >
      <img
        src={getArticleImage(article)}
        alt={article.title}
        title={article.title}
        className="absolute inset-0 h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/60 to-transparent" />
      <div className="relative p-5 md:p-6">
        <span className="inline-flex items-center rounded-md bg-accent-500 px-2 py-0.5 text-xs font-label font-medium text-accent-950">
          {article.categoryName}
        </span>
        <h3 className="mt-3 line-clamp-2 font-heading text-lg font-semibold leading-snug text-background-50 md:text-xl">
          {article.title}
        </h3>
        <p className="mt-2 line-clamp-2 text-sm leading-6 text-background-200">
          {article.summary}
        </p>
        <div className="mt-3 flex items-center gap-3 text-xs text-background-300">
          <span>来源：{article.source}</span>
          <span>·</span>
          <span>{formatDate(article.publishedAt)}</span>
        </div>
      </div>
    </Link>
  );
}

function MiniCard({ article, order }: { article: Article; order: number }) {
  return (
    <Link
      to={`/detail/${article.id}`}
      className="group flex items-stretch gap-4 rounded-lg border border-background-200 bg-background-50 p-3 transition-colors hover:border-primary-300 hover:bg-primary-50/50 animate-fade-up cursor-pointer"
      style={{ animationDelay: `${(order + 1) * 70}ms` }}
    >
      <div className="h-20 w-28 shrink-0 overflow-hidden rounded-md md:h-[86px] md:w-32">
        <img
          src={getArticleImage(article)}
          alt={article.title}
          title={article.title}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span className="font-label text-primary-600">{article.categoryName}</span>
          <span>{formatDate(article.publishedAt)}</span>
        </div>
        <h3 className="mt-1 line-clamp-2 text-sm font-medium leading-6 text-foreground-900 transition-colors group-hover:text-primary-600">
          {article.title}
        </h3>
      </div>
    </Link>
  );
}